import {
	AlbumResults,
	CurrentRefinements,
	DownloadAlert,
	Filters,
	SearchBox,
	SendAnalytics,
	SortBy,
	TrackResults,
	TryAudioSearch,
} from '@/components/pages/search'
import { Configure, ConfigureProps, InstantSearch } from 'react-instantsearch'

import { Container } from '@audio-network/componentlibrary'
import type { GetServerSideProps } from 'next'
import Head from 'next/head'
import algoliasearch from 'algoliasearch/lite'
import { createInstantSearchRouterNext } from 'react-instantsearch-router-nextjs'
import formatTitleByLocale from '@/utils/formatting/formatTitleByLocale'
import { getCatalogues } from '@/pages/api/musicapp/_services/catalogueService'
import { getCurrentSession } from '@/pages/api/musicapp/_services/sessionService'
import { getIpAddressFromHeader } from '@/utils/getIpAddressFromHeader'
import getPlaylist from '@/pages/api/musicapp/_services/playlistService'
import { getTrackRestrictedAttributes } from './restrictedTrackAttributesHelper'
import { getTracks } from '@/pages/api/musicapp/_services/musicService'
import { sendAnalytics } from '@audio-network/analytics'
import singletonRouter from 'next/router'
import styles from './index.module.css'
import useAiSearchStore from '@/stores/aiSearchStore'
import { useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import { withGlobalTranslations } from '@/utils/i18n/withGlobalTranslations'

const searchClient = algoliasearch(
	process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
	process.env.NEXT_PUBLIC_ALGOLIA_API_KEY
)

export default function Search({
	locale,
	playlistTracks,
	user,
	viewableCatalogues,
}: {
	locale: string
	playlistTracks: Track[]
	user: User
	viewableCatalogues: number[]
}): JSX.Element {
	const { t } = useTranslation(['search'])
	const trackIndex = `${process.env.NEXT_PUBLIC_ALGOLIA_INDEX}`
	const catalogueFilters = `Album.Catalogue.Id = ${viewableCatalogues.join(
		' OR Album.Catalogue.Id = '
	)}`

	const { aiSearchFilters, blockAiSearch, searchByAudio } = useAiSearchStore()

	const configureProps = {
		filters: aiSearchFilters
			? `${catalogueFilters} AND ${aiSearchFilters}`
			: `${catalogueFilters}`,
		hitsPerPage: 100,
		restrictSearchableAttributes: getTrackRestrictedAttributes(locale),
	} as ConfigureProps

	useEffect(() => {
		sendAnalytics({
			noun: 'Search Page',
			pastTenseVerb: 'Viewed',
		})
	}, [])

	const hideResults = blockAiSearch && searchByAudio

	return (
		<>
			<Head>
				<title>{formatTitleByLocale(locale, t('title'))}</title>
			</Head>
			<InstantSearch
				searchClient={searchClient}
				indexName={trackIndex}
				routing={{
					router: createInstantSearchRouterNext({
						singletonRouter,
						routerOptions: {
							cleanUrlOnDispose: false,
						},
					}),
				}}
			>
				<SendAnalytics />
				<Configure {...configureProps} />
				<div className={styles.header}>
					<DownloadAlert user={user} />
					<SearchBox user={user} />
					<Filters locale={locale} />
					<Container className={styles.refinements} maxWidth="large">
						<CurrentRefinements />
						<SortBy />
					</Container>
				</div>
				{hideResults && <TryAudioSearch />}
				{!hideResults && (
					<>
						<AlbumResults locale={locale} />
						<TrackResults
							playlistTracks={playlistTracks}
							trackIndex={trackIndex}
							user={user}
						/>
					</>
				)}
			</InstantSearch>
		</>
	)
}

export const getServerSideProps: GetServerSideProps = async ({
	locale,
	res,
	req,
}) => {
	const trackIds = (await getPlaylist('new-releases', locale)).trackIds.split(
		','
	)
	const playlistTracks = await getTracks(trackIds)

	const ipAddress = getIpAddressFromHeader(
		req.headers,
		req.socket.remoteAddress
	)

	const user: User = await getCurrentSession(req, res)

	const viewableCatalogues = await getCatalogues(ipAddress)

	return {
		props: {
			...(await withGlobalTranslations(locale, ['search', 'trackrow'])),
			locale,
			playlistTracks,
			user,
			viewableCatalogues,
		},
	}
}
